/**
 * Ayuda de la vista Comisión (`/admin/ayuda`).
 *
 * Guía por página de lo que la Comisión puede hacer en el portal. Solo
 * visible en la vista Comisión (el link "Ayuda" del menú apunta aquí
 * cuando la vista activa es comisión). Acordeón nativo, mobile-first.
 *
 * Lenguaje usuario-facing: "casa" (no lote), "Comisión" (no directiva).
 */
import { AcordeonItem } from '../../components/ui/AcordeonItem';

export function AyudaComision() {
  return (
    <div className="max-w-2xl py-8">
      <h2 className="text-xl font-medium text-text-primary mb-1">
        Ayuda — Comisión
      </h2>
      <p className="text-sm text-text-muted mb-4">
        Qué puedes hacer en cada pantalla de la vista Comisión. Toca cada
        sección para ver el detalle.
      </p>

      {/* === Cómo entrar === */}
      <AcordeonItem titulo="¿Cómo entro a la vista Comisión?">
        <p>
          Arriba a la derecha, en el menú de tu nombre, hay un selector de
          vista. Elige <strong>Comisión</strong> y el portal cambia a las
          pantallas de gestión del barrio. Para volver a tu vista de vecino,
          elige <strong>Vecino</strong> en el mismo selector.
        </p>
        <p>
          En el celular, la barra de abajo cambia y muestra los accesos de la
          Comisión (Solicitudes, Validaciones, Documentos, Avisos…).
        </p>
      </AcordeonItem>

      {/* === Solicitudes de acceso === */}
      <AcordeonItem titulo="Solicitudes de acceso — dar entrada a un vecino al portal">
        <p>
          Cuando una persona pide acceso al portal, su solicitud llega acá, en
          la pestaña <strong>En revisión</strong>. Las pestañas{' '}
          <strong>Validadas</strong> y <strong>No validadas</strong> muestran
          las ya resueltas.
        </p>
        <p>
          El botón <strong>Revisar</strong> abre una ventana donde confirmas
          los datos antes de dar acceso. <em>Revisar no aprueba de inmediato</em>
          — primero revisas, y dentro de la ventana el botón{' '}
          <strong>Validar</strong> es el que confirma. En esa ventana puedes:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Corregir el nombre</strong> si la persona lo escribió con
            error o incompleto (el RUT no se cambia acá).
          </li>
          <li>
            <strong>Confirmar la casa</strong> (si el texto que escribió no
            calza con una casa real, la eliges del listado).
          </li>
          <li>
            <strong>Elegir el tipo</strong>: propietario que vive, propietario
            que arrienda (no reside), arrendatario o comodatario.
          </li>
          <li>
            <strong>Fecha desde</strong> cuándo habita/posee (formato
            dd-mm-aaaa).
          </li>
          <li>
            <strong>Responsable de pago de cuotas</strong>: márcalo si esta
            persona es quien paga las cuotas de la casa. Solo puede haber un
            responsable activo por casa; si marcas a uno nuevo, el anterior se
            libera solo.
          </li>
        </ul>
        <p>
          Al <strong>Validar</strong>, el sistema crea la cuenta de la persona
          y le manda un correo para entrar al portal. Si en cambio{' '}
          <strong>No validas</strong>, debes escribir un motivo (queda como
          registro; el correo al solicitante no expone ese motivo).
        </p>
      </AcordeonItem>

      {/* === Validaciones === */}
      <AcordeonItem titulo="Validaciones — aprobar residentes, vehículos y accesos">
        <p>
          Cuando un vecino agrega un residente, un vehículo o pide acceso de
          citofonía/WiFi, queda <strong>pendiente de validación</strong> y
          aparece en esta pantalla, agrupado por tipo.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Validar</strong> aprueba el residente/vehículo/acceso.
          </li>
          <li>
            <strong>Rechazar</strong> lo descarta (puedes indicar el motivo).
          </li>
          <li>
            Puedes <strong>validar varios a la vez</strong> seleccionándolos
            (validación masiva), útil cuando un vecino completó su onboarding y
            cargó todo junto.
          </li>
        </ul>
        <p>
          Al validar, al vecino le aparece la confirmación al instante en su
          campanita del portal. El <strong>correo</strong> de confirmación se
          envía agrupado un rato después (no uno por uno, para no llenarle la
          bandeja) — puede tardar hasta ~media hora; es normal.
        </p>
      </AcordeonItem>

      {/* === Consulta de vecinos === */}
      <AcordeonItem titulo="Consulta de vecinos — la ficha completa de una casa">
        <p>
          Busca por <strong>número de casa, patente, RUT, nombre</strong> o el
          código de un llavero o control, y abre la ficha de esa casa. Sirve
          cuando alguien llama con un problema y necesitas ver todo de una:
          quién vive ahí, cómo va con sus pagos, qué vehículos y recursos
          tiene, y qué accesos están activos.
        </p>
        <p>
          En cada persona verás si <strong>llegó a usar el portal</strong>. El
          caso que conviene mirar es <em>"Nunca ha entrado"</em>: la cuenta
          está creada pero el vecino no ingresó todavía, así que probablemente
          necesita ayuda para entrar por primera vez.
        </p>
        <p>
          También verás el <strong>slot de citofonía</strong> y el{' '}
          <strong>wifi</strong> de cada persona. Si dice{' '}
          <em>"por programar"</em>, la Comisión ya lo validó pero todavía no se
          carga en el equipo — ese trabajo lo hace el tesorero desde
          Reprogramación.
        </p>
        <p>
          Esta pantalla es <strong>solo de consulta</strong>: no cobra, no
          modifica ni da de baja nada.
        </p>
      </AcordeonItem>

      {/* === Avisos === */}
      <AcordeonItem titulo="Avisos — comunicar algo a todo el barrio">
        <p>
          Publica un aviso (título + texto) y se envía a{' '}
          <strong>todos los vecinos</strong> con cuenta: les llega por correo y
          les aparece en la campanita del portal. Úsalo para comunicados
          generales (corte de agua, reunión, recordatorios).
        </p>
        <p>
          El envío es inmediato. Revisa bien el texto antes de publicar — el
          aviso sale a todos de una vez.
        </p>
      </AcordeonItem>

      {/* === Documentos === */}
      <AcordeonItem titulo="Documentos — publicar reglamentos, actas, etc.">
        <p>
          Subes documentos del barrio (reglamento, actas, instructivos) con una{' '}
          <strong>vigencia</strong> (fecha desde y, si corresponde, hasta). Los
          vecinos los ven en su sección Documentos. Las fechas se ingresan en
          formato dd-mm-aaaa.
        </p>
      </AcordeonItem>

      {/* === Tenencias / ex-vecino === */}
      <AcordeonItem titulo="Tenencias — cuando alguien deja el barrio (ex-vecino)">
        <p>
          Acá cierras la tenencia de quien vendió su casa o terminó su arriendo.
          Al cerrarla, esa persona deja de aparecer como vecino activo y se
          desactivan sus residentes/accesos de esa casa. La persona{' '}
          <strong>no se borra</strong>: queda en el sistema para el historial.
        </p>
        <p>
          La casa queda lista para que entre el nuevo ocupante (que solicitará
          acceso y lo validarás como cualquier vecino nuevo).
        </p>
        <p>
          <strong>Ojo con el caso distinto:</strong> si la tenencia{' '}
          <em>nunca debió existir</em> —por ejemplo, alguien quedó cargado como
          propietario siendo en realidad el cónyuge— marca la casilla{' '}
          <em>"esta tenencia nunca debió existir"</em>. Ahí no se va nadie de la
          casa: los demás residentes <strong>pasan a la tenencia que queda</strong>{' '}
          con sus llaveros, controles y accesos intactos, y la persona no se
          marca como ex-vecino si sigue viviendo ahí. Sin esa casilla, corregir
          un dato mal cargado daría de baja a media casa.
        </p>
        <p>
          <strong>Llaveros y controles:</strong> al cerrar la tenencia quedan
          en la casa para el próximo ocupante. Si quien se fue{' '}
          <em>se los llevó</em>, avísale al tesorero: él los da de baja y
          quedan en Reprogramación para borrarlos de los motores y las puertas.
        </p>
      </AcordeonItem>

      {/* === Suspensiones === */}
      <AcordeonItem titulo="Suspensiones — casas suspendibles y suspendidas">
        <p>
          La Comisión ve aquí, en solo lectura, las casas{' '}
          <strong>suspendibles</strong> (con su responsable y la deuda total)
          y las que están suspendidas o en proceso. Suspender y reactivar lo
          opera el tesorero.
        </p>
        <p>
          Al confirmar el corte, el <strong>WiFi lo corta el sistema</strong>{' '}
          en eWeLink para todos los residentes de la casa, y al reconectar
          devuelve esos mismos accesos. La <strong>citofonía</strong> la quita
          y la vuelve a programar el tesorero en la central: los celulares de
          la casa aparecen en Reprogramación.
        </p>
      </AcordeonItem>

      {/* === Reprogramación === */}
      <AcordeonItem titulo="Reprogramación — lo que falta tocar en los equipos">
        <p>
          Lista de trabajo del tesorero en terreno: citofonía por programar,
          actualizar o eliminar de la central (incluidos los celulares de casas
          que se suspenden o se reactivan), y{' '}
          <strong>llaveros y controles por desprogramar</strong>.
        </p>
        <p>
          Un llavero o control dado de baja (perdido, robado, dañado o llevado
          por alguien que dejó la casa) <strong>sigue abriendo</strong> hasta
          que se borra a mano de los motores y de las puertas. Por eso queda en
          esta lista hasta que el tesorero lo marca como desprogramado. Si la
          lista crece, conviene recordárselo.
        </p>
        <p>
          Para la Comisión esta pantalla es <strong>solo de consulta</strong>.
        </p>
      </AcordeonItem>

      {/* === Eliminación de cuenta (Ley 21.719) === */}
      <AcordeonItem titulo="Eliminación de cuenta (Ley 21.719)">
        <p>
          Si un vecino pide eliminar su cuenta (derecho de la Ley 21.719), la
          solicitud llega a esta cola. Al ejecutarla, el sistema{' '}
          <strong>anonimiza</strong> a la persona (no borra los registros
          históricos contables, que son obligatorios, pero quita sus datos
          personales). Respeta la separación entre dueño y arrendatario.
        </p>
      </AcordeonItem>

      {/* === Correos === */}
      <AcordeonItem titulo="¿Cuándo le llegan los correos al vecino?">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Inmediatos:</strong> aviso publicado, acceso al portal
            aprobado, pago procesado.
          </li>
          <li>
            <strong>Agrupados (digest):</strong> las validaciones
            (residente/vehículo/acceso) salen juntas un rato después — la
            campanita es instantánea, el correo puede tardar hasta ~media hora.
          </li>
        </ul>
        <p>
          Si un vecino dice que no le llegó algo de validación al instante,
          probablemente sea esto — el correo agrupado sale en la próxima
          ventana de envío.
        </p>
      </AcordeonItem>
    </div>
  );
}
